import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InventoryGrid from "@/components/InventoryGrid";
import { resolvePodPhoto } from "@/components/PodNetworkSection";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

/**
 * Individual pod page. Active pods can accept bookings (uses global cart flow).
 * Opening-Soon pods show a waitlist form + preview of the network catalog.
 */
export default function PodDetail() {
  const { slug } = useParams();
  const cart = useCart();
  const [pod, setPod] = useState(null);
  const [items, setItems] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (p) => setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => {},
        { enableHighAccuracy: false, timeout: 6000, maximumAge: 60_000 }
      );
    }
  }, []);

  useEffect(() => {
    const q = coords ? `?lat=${coords.lat}&lng=${coords.lng}` : "";
    api.get(`/pods/${slug}${q}`)
      .then(({ data }) => {
        setPod(data);
        // Bind cart to this pod so bookings inherit the correct pod_slug
        if (data.status === "active" && cart.setPodSlug) cart.setPodSlug(data.slug);
      })
      .catch(() => setNotFound(true));
  }, [slug, coords, cart]);

  useEffect(() => {
    // Load catalog for all pods so opening-soon can preview
    const params = new URLSearchParams({ start_date: cart.startDate, days: String(cart.rentalDays) });
    api.get(`/inventory?${params.toString()}`).then(({ data }) => setItems(data)).catch(() => {});
  }, [cart.startDate, cart.rentalDays]);

  if (notFound) {
    return (
      <div className="bg-brand-slate min-h-screen">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <i className="fas fa-map-location-dot text-6xl text-slate-300 mb-6" />
          <h1 className="text-4xl font-black uppercase text-brand-blue tracking-tightest mb-4">Pod Not Found</h1>
          <p className="text-slate-600 mb-8 font-bold">We couldn&apos;t find a pod with that name.</p>
          <Link to="/#pods" className="btn-primary px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-sm">
            View All Pods
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!pod) {
    return (
      <div className="bg-brand-slate min-h-screen">
        <Navbar />
        <div className="text-center py-32 text-slate-500 font-black uppercase text-xs tracking-widest">
          <i className="fas fa-spinner fa-spin mr-2" /> Loading pod…
        </div>
      </div>
    );
  }

  const isActive = pod.status === "active";
  return (
    <div className="bg-brand-slate min-h-screen" data-testid={`pod-detail-${pod.slug}`}>
      <Navbar />

      {/* HERO */}
      <header className="relative h-[420px] md:h-[520px] flex items-end overflow-hidden">
        <img src={resolvePodPhoto(pod.photo_url)} alt={pod.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D2240] via-[#0D2240]/80 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 pb-10 w-full">
          <Link to="/#pods" className="inline-flex items-center gap-2 text-white/80 hover:text-brand-gold text-[10px] font-black uppercase tracking-[0.3em] mb-6" data-testid="pod-back-link">
            <i className="fas fa-arrow-left" /> Back to Pod Network
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {isActive ? (
              <span className="bg-emerald-500 text-white text-[10px] font-black uppercase tracking-[0.3em] px-4 py-1.5 rounded-full">
                <i className="fas fa-circle text-[6px] mr-1.5 animate-pulse" /> Live Now
              </span>
            ) : (
              <span className="bg-brand-gold text-brand-blue text-[10px] font-black uppercase tracking-[0.3em] px-4 py-1.5 rounded-full">
                <i className="fas fa-hourglass-half mr-1.5" /> Opening {pod.launch_date ? new Date(pod.launch_date + "T00:00:00").toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "Soon"}
              </span>
            )}
            <span className="bg-white/10 backdrop-blur text-white text-[10px] font-black uppercase tracking-[0.3em] px-4 py-1.5 rounded-full border border-white/20">
              {pod.neighborhood}
            </span>
            {pod.distance_miles != null && (
              <span className="bg-white/10 backdrop-blur text-white text-[10px] font-black uppercase tracking-[0.3em] px-4 py-1.5 rounded-full border border-white/20">
                <i className="fas fa-location-arrow mr-1.5" /> {pod.distance_miles} mi away
              </span>
            )}
          </div>
          <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tightest text-white leading-none mb-3">
            {pod.name}
          </h1>
          <p className="text-white/90 font-bold text-sm md:text-lg max-w-2xl">{pod.tagline}</p>
        </div>
      </header>

      {/* INFO STRIP */}
      <section className="bg-white border-b border-slate-100 py-6">
        <div className="max-w-7xl mx-auto px-4 grid sm:grid-cols-3 gap-4 text-center sm:text-left">
          <InfoBlock icon="fa-location-dot" label="Address" value={pod.address} />
          <InfoBlock icon="fa-clock" label="Hours" value={pod.hours || "24/7 Self-Serve"} />
          <InfoBlock icon="fa-key" label="Access" value="6-digit PIN via SMS" />
        </div>
      </section>

      {/* ACTIVE → INVENTORY GRID */}
      {isActive && (
        <section className="py-24 bg-brand-slate">
          <div className="max-w-7xl mx-auto px-4">
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-2">
              Rent From This Pod
            </p>
            <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tightest text-brand-blue mb-8">
              {pod.neighborhood} <span className="text-brand-red">Inventory</span>
            </h2>
            <InventoryGrid items={items} />
          </div>
        </section>
      )}

      {/* OPENING SOON → WAITLIST + CATALOG PREVIEW */}
      {!isActive && (
        <>
          <WaitlistBlock pod={pod} />
          <section className="py-20 bg-brand-slate">
            <div className="max-w-7xl mx-auto px-4">
              <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-2">
                Preview
              </p>
              <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tightest text-brand-blue mb-8">
                Catalog at Launch
              </h2>
              <ComingSoonCatalog items={items} slug={pod.slug} />
            </div>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
}

function InfoBlock({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3 justify-center sm:justify-start">
      <div className="w-10 h-10 rounded-xl bg-brand-blue text-brand-gold flex items-center justify-center flex-shrink-0">
        <i className={`fas ${icon}`} />
      </div>
      <div className="min-w-0">
        <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400">{label}</p>
        <p className="text-brand-blue font-black uppercase tracking-tight text-sm truncate">{value}</p>
      </div>
    </div>
  );
}

function WaitlistBlock({ pod }) {
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/pods/${pod.slug}/waitlist`, { email, zip_code: zip || null });
      setJoined(true);
      toast.success(data.message || "You're on the list!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't join the waitlist. Try again?");
    } finally { setBusy(false); }
  };

  if (joined) {
    return (
      <section className="py-20 bg-white border-b border-slate-100" data-testid="waitlist-joined">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <i className="fas fa-check text-3xl" />
          </div>
          <h2 className="text-3xl md:text-4xl font-black uppercase text-brand-blue tracking-tightest mb-3">You&apos;re On The List</h2>
          <p className="text-slate-600 font-bold mb-6">We&apos;ll email you the moment {pod.name} goes live.</p>
          <Link to="/#pods" className="text-brand-red font-black uppercase tracking-widest text-xs hover:underline">
            Check other pods <i className="fas fa-arrow-right ml-1.5" />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white border-b border-slate-100" data-testid="pod-waitlist-section">
      <div className="max-w-2xl mx-auto px-4 text-center">
        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-3">
          Get Early Access
        </p>
        <h2 className="text-3xl md:text-4xl font-black uppercase text-brand-blue tracking-tightest mb-3">
          Be First At {pod.neighborhood}
        </h2>
        <p className="text-slate-600 font-bold mb-8">
          Drop your email and we&apos;ll notify you the moment this pod unlocks. Founders get 20% off their first rental.
        </p>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
          <input
            type="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 rounded-2xl border border-slate-200 px-5 py-4 text-sm font-bold focus:outline-none focus:border-brand-gold"
            data-testid="waitlist-email-input"
          />
          <input
            type="text"
            placeholder="ZIP (optional)"
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            className="w-full sm:w-32 rounded-2xl border border-slate-200 px-5 py-4 text-sm font-bold focus:outline-none focus:border-brand-gold"
            data-testid="waitlist-zip-input"
          />
          <button
            type="submit"
            disabled={busy}
            className="btn-primary px-8 py-4 rounded-2xl font-black uppercase text-[11px] tracking-widest disabled:opacity-50"
            data-testid="waitlist-submit-button"
          >
            {busy ? <><i className="fas fa-spinner fa-spin mr-2" /> Joining</> : <>Notify Me <i className="fas fa-bell ml-2" /></>}
          </button>
        </form>
      </div>
    </section>
  );
}


function ComingSoonCatalog({ items, slug }) {
  if (!items || items.length === 0) {
    return (
      <div className="text-center py-16 text-slate-500 font-black uppercase tracking-widest text-xs">
        <i className="fas fa-spinner fa-spin mr-2" /> Loading catalog…
      </div>
    );
  }
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="coming-soon-grid">
        {items.map((item) => (
          <div
            key={item.id}
            className="relative bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm"
            data-testid={`coming-soon-item-${item.id}`}
          >
            {/* Photo */}
            <div className="relative h-40 bg-slate-100">
              {item.image_url ? (
                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-4xl text-slate-300">
                  <i className={`fas ${item.icon || "fa-box"}`} />
                </div>
              )}
              {/* Overlay badge */}
              <div className="absolute inset-0 bg-brand-blue/70 flex flex-col items-center justify-center text-center px-3">
                <span className="bg-brand-gold text-brand-blue text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full mb-1.5">
                  <i className="fas fa-hourglass-half mr-1.5" /> Available at Launch
                </span>
                <p className="text-white text-[10px] font-black uppercase tracking-widest">Join Waitlist</p>
              </div>
            </div>
            {/* Body */}
            <div className="p-3">
              <p className="font-black text-brand-blue text-sm uppercase tracking-tight leading-tight mb-1 truncate">{item.name}</p>
              <p className="text-[10px] text-slate-500 font-bold">${item.price_per_day.toFixed(2)}/day</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-10 text-center">
        <a
          href={`#pod-waitlist-section`}
          onClick={(e) => {
            e.preventDefault();
            document.querySelector('[data-testid="pod-waitlist-section"]')?.scrollIntoView({ behavior: "smooth" });
          }}
          className="btn-primary inline-flex items-center px-8 py-4 rounded-2xl font-black uppercase text-xs tracking-widest"
          data-testid={`join-${slug}-waitlist-cta`}
        >
          <i className="fas fa-bell mr-2" /> Notify Me When This Pod Opens
        </a>
      </div>
    </div>
  );
}
